<template>
  <div class="page-container">
    <div class="page-header">
      <h1 class="page-title">{{ isEdit ? '編輯最新消息' : '新增最新消息' }}</h1>
      <router-link class="btn btn-secondary" :to="{ name: 'WebsiteBlogIndex' }">返回列表</router-link>
    </div>

    <div v-if="loading" class="loading-state"><div class="spinner" /><p>載入中...</p></div>
    <form v-else class="form-card" @submit.prevent="submit">
      <h3 class="section-title first">文章內容</h3>
      <div class="form-grid">
        <div class="form-group">
          <label>標題 <span class="req">*</span></label>
          <input v-model="form.title" class="form-control" required />
        </div>
        <div class="form-group">
          <label>分類</label>
          <select v-model="form.category" class="form-control">
            <option value="product">產品動態</option>
            <option value="guide">使用教學</option>
            <option value="announcement">公告</option>
          </select>
        </div>
        <div class="form-group">
          <label>狀態</label>
          <select v-model="form.status" class="form-control">
            <option value="draft">草稿</option>
            <option value="published">發佈</option>
            <option value="archived">封存</option>
          </select>
        </div>
      </div>

      <div class="form-group">
        <label>摘要</label>
        <textarea v-model="form.excerpt" class="form-control" rows="2" />
      </div>

      <div class="form-group">
        <label>封面圖</label>
        <ImageUploader
          v-model="form.coverImageUrl"
          :upload-fn="uploadImage"
          shape="card"
          aspect-ratio="16 / 9"
          :crop-width="1200"
          :crop-height="675"
          accept="image/jpeg,image/png,image/webp"
          :max-size-mb="5"
          hint="輸出 1200 × 675 px（16:9），可拖曳／縮放裁切"
          size-hint="支援 JPEG／PNG／WebP，單檔上限 5MB；預覽與前台列表／文章頁同為 16:9"
          placeholder="上傳封面圖"
          upload-label="上傳封面"
          replace-label="更換封面"
        />
      </div>

      <div class="form-group">
        <label>正文</label>
        <div class="quill-wrapper">
          <quillEditor
            :key="quillKey"
            :value="form.content"
            @update:value="form.content = $event"
            :options="quillOptions"
            class="quill-editor"
          />
        </div>
      </div>

      <h3 class="section-title">SEO／網址</h3>
      <div class="form-group">
        <label>Slug</label>
        <input
          v-model="form.slug"
          class="form-control"
          placeholder="空白則依標題產生，例如 product-update-2026"
        />
        <p class="field-hint">
          前台完整網址：
          <code>{{ fullArticleUrl }}</code>
        </p>
        <p class="field-hint muted">僅使用小寫英文、數字與連字號；發佈後若修改 slug，舊網址會 301 導向新網址。</p>
      </div>
      <div class="form-group">
        <label>Canonical URL</label>
        <input
          v-model="canonicalUrlModel"
          class="form-control"
          :placeholder="fullArticleUrl"
        />
        <p class="field-hint muted">
          告訴搜尋引擎「這篇文章的正式網址」。空白時預設為上方前台網址（依 slug）。多數情況不需改；僅在內容與其他網址重複、需指定正式版時再調整。
        </p>
      </div>
      <div class="form-group">
        <label>SEO Title</label>
        <input v-model="form.seoTitle" class="form-control" placeholder="空白則使用文章標題" />
      </div>
      <div class="form-group">
        <label>SEO Description</label>
        <textarea v-model="form.seoDescription" class="form-control" rows="2" placeholder="空白則使用摘要" />
      </div>
      <div class="form-group">
        <label>OG Image（社群分享圖）</label>
        <ImageUploader
          v-model="form.ogImage"
          :upload-fn="uploadImage"
          shape="card"
          aspect-ratio="1200 / 630"
          :crop-width="1200"
          :crop-height="630"
          accept="image/jpeg,image/png,image/webp"
          :max-size-mb="5"
          hint="輸出 1200 × 630 px（約 1.91:1），可拖曳／縮放裁切"
          size-hint="支援 JPEG／PNG／WebP，單檔上限 5MB；預覽與 Facebook／LINE 分享比例一致。未上傳時將使用封面圖"
          placeholder="上傳 OG Image（選填）"
          upload-label="上傳 OG 圖"
          replace-label="更換 OG 圖"
        />
      </div>

      <div class="form-actions">
        <button type="submit" class="btn btn-primary" :disabled="saving">
          {{ saving ? '儲存中…' : '儲存' }}
        </button>
      </div>
    </form>
  </div>
</template>

<script>
import { quillEditor } from 'vue3-quill'
import 'quill/dist/quill.snow.css'
import ImageUploader from '@/components/ImageUploader.vue'
import { websiteBlogService } from '@/api/modules/website.js'
import { showSuccess, showError } from '@/utils/swal.js'

const WWW_SITE_URL = (process.env.VUE_APP_WWW_URL || 'https://www.lockline.io').replace(/\/$/, '')

export default {
  name: 'WebsiteBlogForm',
  components: { quillEditor, ImageUploader },
  data() {
    return {
      loading: false,
      saving: false,
      quillKey: 0,
      form: {
        title: '',
        slug: '',
        excerpt: '',
        coverImageUrl: '',
        content: '',
        category: 'product',
        status: 'draft',
        seoTitle: '',
        seoDescription: '',
        ogImage: '',
        canonicalUrl: '',
      },
      quillOptions: {
        theme: 'snow',
        modules: {
          toolbar: [
            [{ header: [1, 2, 3, false] }],
            ['bold', 'italic', 'underline', 'strike'],
            [{ list: 'ordered' }, { list: 'bullet' }],
            ['link', 'image', 'blockquote'],
            ['clean'],
          ],
        },
      },
    }
  },
  computed: {
    isEdit() {
      return !!this.$route.params.id
    },
    slugPreview() {
      const raw = (this.form.slug || '').trim()
      if (raw) return raw
      return this.slugify(this.form.title) || '{slug}'
    },
    fullArticleUrl() {
      return `${WWW_SITE_URL}/blog/${this.slugPreview}`
    },
    /** 空白時跟著 slug 預覽；有值則用自訂 canonical */
    canonicalUrlModel: {
      get() {
        return this.form.canonicalUrl || this.fullArticleUrl
      },
      set(value) {
        const next = String(value || '').trim()
        this.form.canonicalUrl = next && next !== this.fullArticleUrl ? next : ''
      },
    },
  },
  async mounted() {
    if (this.isEdit) await this.load()
  },
  methods: {
    slugify(input) {
      return String(input || '')
        .trim()
        .toLowerCase()
        .replace(/[\s_]+/g, '-')
        .replace(/[^a-z0-9\u4e00-\u9fff-]/g, '')
        .replace(/-+/g, '-')
        .replace(/^-|-$/g, '')
        .slice(0, 180)
    },
    uploadImage(file) {
      return websiteBlogService.uploadImage(file)
    },
    async load() {
      this.loading = true
      try {
        const res = await websiteBlogService.get(this.$route.params.id)
        const item = res?.data || {}
        this.form = {
          title: item.title || '',
          slug: item.slug || '',
          excerpt: item.excerpt || '',
          coverImageUrl: item.coverImageUrl || '',
          content: item.content || '',
          category: item.category || 'product',
          status: item.status || 'draft',
          seoTitle: item.seoTitle || '',
          seoDescription: item.seoDescription || '',
          ogImage: item.ogImage || '',
          canonicalUrl: item.canonicalUrl || '',
        }
        // 強制 remount，確保 Quill 以載入後的 HTML 初始化
        this.quillKey += 1
      } catch (e) {
        showError(e?.response?.data?.message || '載入失敗')
        this.$router.push({ name: 'WebsiteBlogIndex' })
      } finally {
        this.loading = false
      }
    },
    async submit() {
      this.saving = true
      try {
        const payload = { ...this.form }
        if (!payload.slug) delete payload.slug
        // 空白則送出依 slug 組出的預設正式網址
        payload.canonicalUrl = payload.canonicalUrl || this.fullArticleUrl
        let res
        if (this.isEdit) {
          res = await websiteBlogService.update(this.$route.params.id, payload)
        } else {
          res = await websiteBlogService.create(payload)
        }
        const warning = res?.data?.cacheWarning
        showSuccess(warning ? `已儲存（快取警告：${warning}）` : '已儲存')
        this.$router.push({ name: 'WebsiteBlogIndex' })
      } catch (e) {
        showError(e?.response?.data?.message || '儲存失敗')
      } finally {
        this.saving = false
      }
    },
  },
}
</script>

<style scoped>
@import '~@/assets/scss/components/_list-page-common.scss';
.form-card {
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  padding: 20px;
}
.form-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
}
.form-group { margin-bottom: 14px; }
.form-group label { display: block; font-weight: 600; font-size: 13px; margin-bottom: 4px; }
.req { color: #dc2626; }
.field-hint {
  margin: 6px 0 0;
  font-size: 12px;
  color: #334155;
  word-break: break-all;
}
.field-hint.muted { color: #64748b; }
.field-hint code {
  font-size: 12px;
  background: #f1f5f9;
  padding: 2px 6px;
  border-radius: 4px;
}
.section-title {
  margin: 28px 0 14px;
  padding-top: 20px;
  border-top: 1px solid #e2e8f0;
  font-size: 1rem;
}
.section-title.first {
  margin-top: 0;
  padding-top: 0;
  border-top: none;
}
.form-actions { margin-top: 16px; }
.quill-wrapper { background: #fff; }
.quill-editor :deep(.ql-container) { min-height: 220px; }
@media (max-width: 768px) {
  .form-grid { grid-template-columns: 1fr; }
}
</style>
